import {useEffect} from 'react';
import {useSearchParams} from '../../../router/compat';

/** Carries the project in context from wherever "Create → Ticket" was used. */
export const CREATE_PROJECT_PARAM = 'create_project_id';

/**
 * ACC-506: When the top-nav "Create → Ticket" item navigates to
 * `/tickets?create=1`, open the ticket-creation modal once on arrival, then
 * strip the param (replace, so back-nav / refresh don't re-open it). Fails
 * closed: if the user lacks create permission the param is still cleaned up but
 * the modal stays shut (the gateway also enforces TICKETS_CREATE).
 *
 * Raised from a project screen, the item also sends `create_project_id`; it is
 * handed to `openCreateModal` as the project to pre-select (null when absent) and
 * stripped along with `create`.
 */
export function useOpenCreateFromQuery(canCreate: boolean, openCreateModal: (defaultProjectId: string | null) => void): void {
	const [searchParams, setSearchParams] = useSearchParams();
	const hasCreateParam = searchParams.get('create') === '1';
	const projectId = searchParams.get(CREATE_PROJECT_PARAM) || null;

	useEffect(() => {
		if (!hasCreateParam) return;
		if (canCreate) openCreateModal(projectId);
		setSearchParams(
			prev => {
				const updated = new URLSearchParams(prev);
				updated.delete('create');
				updated.delete(CREATE_PROJECT_PARAM);
				return updated;
			},
			{replace: true}
		);
		// Run once per arrival with the param present. openCreateModal is stable
		// (a setState updater); excluded to avoid re-firing on parent re-render.
		// projectId is read on that same arrival, and stripped with it.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [hasCreateParam, canCreate, setSearchParams]);
}
